import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, ScanLine, Settings, Lightbulb, UserCog, LogOut, Menu, Bell, HelpCircle } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const baseNavItems = [
  { text: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { text: 'Email Scanner', icon: ScanLine, path: '/scanner' },
  { text: 'Recommendations', icon: Lightbulb, path: '/recommendations' },
  { text: 'Settings', icon: Settings, path: '/settings' },
];

const adminNavItem = { text: 'Admin', icon: UserCog, path: '/admin' };

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const navItems = user?.role === 'admin' ? [...baseNavItems, adminNavItem] : baseNavItems;
  const pageTitle = navItems.find(i => i.path === location.pathname)?.text || 'Dashboard';

  const sidebar = (
    <div className="h-full flex flex-col bg-white">
      <div className="p-5 border-b">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-7 h-7 text-blue-600" />
          <span className="text-lg font-bold text-blue-600">Rspamd Agent</span>
        </div>
        <p className="text-xs text-gray-500">Rspamd Email Scanner</p>
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1">
        {navItems.map(item => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }} title={item.text}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Icon className="w-5 h-5 flex-shrink-0" /><span className="truncate">{item.text}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
            {user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.username || 'User'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.role || 'user'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-64 flex-shrink-0 border-r bg-white fixed h-full z-20">
        {sidebar}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 shadow-xl z-50">{sidebar}</div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 md:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-10 bg-white border-b h-16 flex items-center px-4 shadow-sm">
          <button className="md:hidden mr-3" onClick={() => setMobileOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold flex-1">{pageTitle}</h1>
          <button className="p-2 rounded-lg hover:bg-gray-100 mr-1"><Bell className="w-5 h-5 text-gray-500" /></button>
          <button className="p-2 rounded-lg hover:bg-gray-100 mr-1"><HelpCircle className="w-5 h-5 text-gray-500" /></button>

          <div className="relative">
            <button onClick={() => setProfileOpen(!profileOpen)} className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </button>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setProfileOpen(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-40 py-1">
                  <button onClick={() => { setProfileOpen(false); navigate('/settings'); }} className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-gray-50">
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                  <hr className="my-1" />
                  <button onClick={() => { setProfileOpen(false); logout(); }} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="p-6">{children}</main>
      </div>
    </div>
  );
}

export default Layout;
